/**
 * 安全的 Web Storage 封装
 * 应对部分移动端环境（如 iOS Safari 无痕模式、部分 App 内嵌 Webview、禁用 Cookie）下
 * 访问 localStorage/sessionStorage 抛出 SecurityError/QuotaExceededError 的问题
 */

class MemoryStorage implements Storage {
  private store: Record<string, string> = {};

  get length(): number {
    return Object.keys(this.store).length;
  }

  clear(): void {
    this.store = {};
  }

  getItem(key: string): string | null {
    return Object.prototype.hasOwnProperty.call(this.store, key) ? this.store[key] : null;
  }

  key(index: number): string | null {
    const keys = Object.keys(this.store);
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    delete this.store[key];
  }

  setItem(key: string, value: string): void {
    this.store[key] = String(value);
  }
}

const createSafeStorage = (type: 'localStorage' | 'sessionStorage'): Storage => {
  try {
    const storage = window[type];
    const testKey = '__storage_test__';
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return storage;
  } catch (error) {
    console.warn(`[storage] ${type} is unavailable, fallback to MemoryStorage:`, error);
    return new MemoryStorage();
  }
};

export const safeLocalStorage: Storage = createSafeStorage('localStorage');
export const safeSessionStorage: Storage = createSafeStorage('sessionStorage');

/**
 * 安全生成 UUID，兼容旧版移动端浏览器及非 HTTPS 环境
 */
export function safeGetUUID(): string {
  try {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (e) {
    // ignore
  }

  // 降级方案
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
